import { useEffect, useState, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Menu, X, ArrowRight, ChevronDown,
  FileText, PencilLine, Globe2, Headphones, Mic, Subtitles, AudioLines,
  ScanText, FileSearch, Languages as LangIcon, ClipboardCheck, Database,
} from "lucide-react";
import { BRAND, LANGUAGE_SERVICES, AI_SERVICES } from "@/data/content";

const ICON_MAP = {
  FileText, PencilLine, Globe2, Headphones, Mic, Subtitles, AudioLines,
  ScanText, FileSearch, Languages: LangIcon, ClipboardCheck, Database,
};

const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services", hasMega: true },
  { label: "Industries", to: "/industries" },
  { label: "Languages", to: "/languages" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

function MegaMenu({ onClose }) {
  return (
    <div
      data-testid="nav-mega-menu"
      className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-[min(1100px,calc(100vw-32px))] bg-white border border-[#E5E7EB] shadow-[0_18px_50px_-20px_rgba(7,29,73,0.22)] rounded-md overflow-hidden"
      onMouseLeave={onClose}
    >
      <div className="grid grid-cols-12">
        {/* Language Services */}
        <div className="col-span-6 p-7 border-r border-[#E5E7EB]">
          <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#1A4E8A] mb-4">
            Language Services
          </p>
          <ul className="grid grid-cols-2 gap-x-4 gap-y-1">
            {LANGUAGE_SERVICES.map((s) => {
              const Icon = ICON_MAP[s.icon] || FileText;
              return (
                <li key={s.slug}>
                  <Link
                    to={`/services#${s.slug}`}
                    onClick={onClose}
                    data-testid={`mega-link-${s.slug}`}
                    className="flex items-start gap-3 px-3 py-2.5 rounded hover:bg-[#F7F8FA] group transition-colors"
                  >
                    <Icon className="w-4 h-4 mt-0.5 text-[#1A4E8A] flex-shrink-0" />
                    <span className="text-[13px] font-semibold text-[#071D49] group-hover:text-[#1A4E8A] leading-tight">
                      {s.title}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        {/* AI Solutions */}
        <div className="col-span-6 p-7 bg-[#F7F8FA]">
          <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#00D1B2] mb-4">
            AI Language Solutions
          </p>
          <ul className="space-y-1">
            {AI_SERVICES.map((s) => {
              const Icon = ICON_MAP[s.icon] || FileSearch;
              return (
                <li key={s.slug}>
                  <Link
                    to={`/services#${s.slug}`}
                    onClick={onClose}
                    data-testid={`mega-link-${s.slug}`}
                    className="flex items-start gap-3 px-3 py-2.5 rounded hover:bg-white border border-transparent hover:border-[#E5E7EB] group transition-all"
                  >
                    <Icon className="w-4 h-4 mt-0.5 text-[#00D1B2] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold text-[#071D49] group-hover:text-[#1A4E8A] leading-tight">
                        {s.shortTitle || s.title}
                      </p>
                      <p className="text-[11.5px] text-[#5B6472] leading-snug mt-0.5 line-clamp-1">
                        {s.description.slice(0, 70)}…
                      </p>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Footer strip */}
      <div className="border-t border-[#E5E7EB] bg-white px-7 py-3 flex items-center justify-between">
        <span className="text-[11px] tracking-wide text-[#5B6472]">
          Human expertise · AI-enhanced precision · 85+ languages
        </span>
        <Link
          to="/contact"
          onClick={onClose}
          data-testid="mega-cta-quote"
          className="text-[12px] font-semibold text-[#1A4E8A] hover:text-[#071D49] inline-flex items-center gap-1.5"
        >
          Request a Quote <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMegaOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  const openMega = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setMegaOpen(true);
  };
  const queueClose = () => {
    closeTimer.current = setTimeout(() => setMegaOpen(false), 120);
  };

  return (
    <header
      data-testid="site-navbar"
      data-scrolled={scrolled ? "true" : "false"}
      className={`fixed left-0 right-0 z-50 transition-all duration-500 ease-out ${
        scrolled
          ? "top-0 bg-white border-b border-[#E5E7EB]/80 shadow-[0_8px_24px_-18px_rgba(7,29,73,0.18)]"
          : "top-0 bg-transparent border-b border-transparent"
      }`}
    >
      <div
        className={`transition-all duration-500 ${
          scrolled ? "bg-white" : "glass-nav-dark"
        }`}
      >
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10 h-16 lg:h-[68px] flex items-center justify-between">
        {/* Logo */}
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img
            src={scrolled ? BRAND.logoColor : BRAND.logoWhite}
            alt="Petraloc"
            className="h-8 lg:h-9 w-auto transition-opacity duration-300"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7 relative">
          {NAV.map((item) =>
            item.hasMega ? (
              <div
                key={item.to}
                className="relative"
                onMouseEnter={openMega}
                onMouseLeave={queueClose}
              >
                <NavLink
                  to={item.to}
                  data-testid={`nav-link-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-1 text-[13.5px] font-semibold tracking-wide transition-colors ${
                      isActive || megaOpen
                        ? "text-[#00D1B2]"
                        : scrolled
                        ? "text-[#222222] hover:text-[#00D1B2]"
                        : "text-white/95 hover:text-[#00D1B2]"
                    }`
                  }
                >
                  {item.label}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${megaOpen ? "rotate-180" : ""}`} />
                </NavLink>
                {megaOpen && <MegaMenu onClose={() => setMegaOpen(false)} />}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-[13.5px] font-semibold tracking-wide transition-colors ${
                    isActive
                      ? "text-[#00D1B2]"
                      : scrolled
                      ? "text-[#222222] hover:text-[#00D1B2]"
                      : "text-white/95 hover:text-[#00D1B2]"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        {/* Desktop CTA */}
        <Link
          to="/contact"
          data-testid="nav-cta-quote"
          className={`max-lg:!hidden lg:inline-flex btn-cta ${scrolled ? "btn-navy" : "btn-primary"}`}
        >
          <span>Get a Quote</span>
          <ArrowRight className="btn-arrow w-3.5 h-3.5" />
        </Link>

        {/* Mobile toggle */}
        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden p-2 ${scrolled ? "text-[#071D49]" : "text-white"}`}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="lg:hidden bg-white border-t border-[#E5E7EB] max-h-[80vh] overflow-y-auto"
          data-testid="nav-mobile-menu"
        >
          <div className="px-6 py-4 flex flex-col gap-0.5">
            {NAV.map((item) =>
              item.hasMega ? (
                <div key={item.to}>
                  <button
                    onClick={() => setMobileServicesOpen((v) => !v)}
                    data-testid="nav-mobile-services-toggle"
                    className="w-full flex items-center justify-between py-3 px-3 text-sm font-semibold text-[#071D49]"
                  >
                    Services
                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
                  </button>
                  {mobileServicesOpen && (
                    <div className="pl-3 pb-3 space-y-3">
                      <div>
                        <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#1A4E8A] mb-2 mt-2">
                          Language Services
                        </p>
                        <ul>
                          {LANGUAGE_SERVICES.map((s) => (
                            <li key={s.slug}>
                              <Link
                                to={`/services#${s.slug}`}
                                className="block py-1.5 text-[13px] text-[#222222] hover:text-[#1A4E8A]"
                              >
                                {s.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#00D1B2] mb-2">
                          AI Language Solutions
                        </p>
                        <ul>
                          {AI_SERVICES.map((s) => (
                            <li key={s.slug}>
                              <Link
                                to={`/services#${s.slug}`}
                                className="block py-1.5 text-[13px] text-[#222222] hover:text-[#1A4E8A]"
                              >
                                {s.shortTitle || s.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `py-3 px-3 text-sm font-semibold transition-colors ${
                      isActive ? "text-[#00D1B2]" : "text-[#222222] hover:text-[#00D1B2]"
                    }`
                  }
                  end={item.to === "/"}
                >
                  {item.label}
                </NavLink>
              )
            )}
            <Link
              to="/contact"
              data-testid="nav-mobile-cta"
              className="mt-3 inline-flex items-center justify-center gap-2 bg-[#071D49] text-white px-5 py-3 rounded-md text-sm font-semibold"
            >
              Get a Quote <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
      </div>
    </header>
  );
}
